import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation } from "../hooks/useResource";
import { ErrorBanner, LoadingState } from "../components/common";
import { usePortalAuth } from "./PortalAuthProvider";

/**
 * Portal sign-in.
 *
 * The failure message is whatever the server said, and the server says one thing
 * for a wrong password, an unknown address, a disabled login and a closed account —
 * so this screen must not try to be more helpful than that. "No account with that
 * email" would tell an outsider which of our customers has a portal.
 *
 * There is no sign-up and no password reset link, because neither exists: an
 * operator creates the login and an operator resets it. Saying so here is the
 * difference between a dead end and a form somebody sits in front of waiting for a
 * reset email that is never coming.
 */
export function PortalLoginPage() {
  const { user, signIn } = usePortalAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const login = useMutation(async () => {
    await signIn(email.trim(), password);
  });

  if (user === undefined) {
    return (
      <Box sx={{ p: 6 }}>
        <LoadingState label="Checking session…" />
      </Box>
    );
  }
  if (user) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from && from !== "/portal/login" ? from : "/portal"} replace />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        p: 3,
        bgcolor: "background.default",
      }}
    >
      <Paper sx={{ p: 4, width: 380, maxWidth: "100%" }} elevation={2}>
        <Typography variant="h5">FieldClients</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
          Your leads
        </Typography>

        <ErrorBanner error={login.error} />

        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            void login.run(undefined);
          }}
        >
          <Stack spacing={2}>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              autoFocus
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              fullWidth
            />
            <Button
              type="submit"
              variant="contained"
              disabled={login.pending || email === "" || password === ""}
            >
              {login.pending ? "Signing in…" : "Sign in"}
            </Button>
          </Stack>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 3 }}>
          Your account contact sets up this login and can issue a new password. Repeated failed
          attempts are rate limited for a few minutes.
        </Typography>
      </Paper>
    </Box>
  );
}
